import React, { useEffect, useState } from 'react';
import { Alert, Button, Group, Loader, Progress, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconCircle, IconClockExclamation, IconX } from '@tabler/icons-react';
import * as Enums from '@/utils/enums';
import { ImportDraft } from '@/services/import-draft/import-draft-model';
import { progressLabel, progressPercent, stageLabel } from '@/services/import-draft/import-draft-helpers';

interface Props {
  draft: ImportDraft;
  onRetry: () => void;
  onCancel: () => void;
  onReadAsReport?: () => void;
  busy?: boolean;
  startedAt?: number | null;
}

export const SLOW_AFTER_MS = 90 * 1000;

const TABLE_STAGES = [
  { stage: Enums.ImportDraftStage.Parsing, label: 'Reading the file' },
  { stage: Enums.ImportDraftStage.Inferring, label: 'Matching columns' },
  { stage: Enums.ImportDraftStage.Ready, label: 'Checking the results' },
];

const EXTRACT_STAGES = [
  { stage: Enums.ImportDraftStage.Parsing, label: 'Reading the file' },
  { stage: Enums.ImportDraftStage.Extracting, label: 'Extracting records' },
  { stage: Enums.ImportDraftStage.Ready, label: 'Checking values against the file' },
];

const ORDER: Record<number, number> = {
  [Enums.ImportDraftStage.Uploaded]: 0,
  [Enums.ImportDraftStage.Parsing]: 1,
  [Enums.ImportDraftStage.Inferring]: 2,
  [Enums.ImportDraftStage.Extracting]: 2,
  [Enums.ImportDraftStage.Ready]: 3,
};

function stageState(current: number, stage: number): 'done' | 'active' | 'todo' {
  if (current === Enums.ImportDraftStage.Failed) return 'todo';
  const now = ORDER[current] ?? 0;
  const target = ORDER[stage] ?? 0;
  if (now > target || current === Enums.ImportDraftStage.Ready) return 'done';
  if (now === target) return 'active';
  return 'todo';
}

function useElapsed(startedAt: number | null | undefined, active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active || !startedAt) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active, startedAt]);
  return startedAt ? Math.max(0, now - startedAt) : 0;
}

export default function ProcessingCard({ draft, onRetry, onCancel, onReadAsReport, busy, startedAt }: Props) {
  const failed = draft.Stage === Enums.ImportDraftStage.Failed;
  const elapsed = useElapsed(startedAt, !failed);
  const slow = !failed && elapsed >= SLOW_AFTER_MS;
  const cancelDisabled = busy || draft.Stage === Enums.ImportDraftStage.Committing;
  const extracting = draft.Stage === Enums.ImportDraftStage.Extracting || draft.Mode === Enums.ImportDraftMode.Extract;
  const stages = extracting ? EXTRACT_STAGES : TABLE_STAGES;
  const percent = progressPercent(draft);
  const label = progressLabel(draft);

  return (
    <Stack gap="md" data-testid="processing-card">
      <Text fw={600}>{draft.SourceFileName}</Text>
      <Text size="sm" c="dimmed">{stageLabel(draft.Stage)}</Text>
      {draft.ModeReason && <Text size="sm">{draft.ModeReason}</Text>}

      <Stack gap="xs">
        {stages.map(s => {
          const state = stageState(draft.Stage, s.stage);
          return (
            <Group key={s.stage} gap="sm">
              {state === 'done' && <ThemeIcon size="sm" color="green" radius="xl"><IconCheck size={12} /></ThemeIcon>}
              {state === 'active' && <Loader size="xs" />}
              {state === 'todo' && <ThemeIcon size="sm" color="gray" variant="light" radius="xl"><IconCircle size={10} /></ThemeIcon>}
              <Text size="sm" c={state === 'todo' ? 'dimmed' : undefined}>{s.label}</Text>
            </Group>
          );
        })}
      </Stack>

      {draft.Stage === Enums.ImportDraftStage.Extracting && (
        <Stack gap={4} data-testid="extraction-progress">
          <Progress value={percent ?? 0} animated striped size="lg" />
          <Text size="xs" c="dimmed">{label ?? 'Preparing the file…'}</Text>
        </Stack>
      )}

      {failed && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="The file could not be processed">
          <Text size="sm">{draft.Error || 'An error occurred while processing the file.'}</Text>
          <Group mt="sm" gap="xs">
            <Button size="xs" onClick={onRetry} loading={busy}>Try again</Button>
            {onReadAsReport && draft.Mode !== Enums.ImportDraftMode.Extract && (
              <Button size="xs" variant="light" onClick={onReadAsReport} disabled={busy}>Process as a report instead</Button>
            )}
            <Button size="xs" variant="subtle" color="gray" onClick={onCancel} disabled={busy}>Start over</Button>
          </Group>
        </Alert>
      )}

      {slow && (
        <Alert color="yellow" icon={<IconClockExclamation size={16} />} title="This is taking longer than usual" data-testid="processing-slow">
          <Text size="sm">Still busy: {stageLabel(draft.Stage).toLowerCase()}. If nothing changes in a few minutes, cancel and try again.</Text>
          <Group mt="sm" gap="xs">
            <Button size="xs" variant="default" leftSection={<IconX size={14} />} onClick={onCancel} disabled={cancelDisabled} data-testid="cancel-processing">
              Cancel
            </Button>
          </Group>
        </Alert>
      )}

      {!failed && !slow && (
        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <Text size="xs" c="dimmed">
            {extracting
              ? 'Large reports can take a few minutes. You can leave this page and processing will continue in the background.'
              : 'This usually takes a few seconds, and up to a minute for large files.'}
          </Text>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconX size={14} />}
            onClick={onCancel}
            disabled={cancelDisabled}
            data-testid="cancel-processing"
          >
            Cancel
          </Button>
        </Group>
      )}
    </Stack>
  );
}
